import { ConfirmDialog } from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import { Answer } from './components/Answer/Answer';
import { SurveyAnswer } from './components/SurveyAnswer';
import { normalizeQuestionId } from './utils';
import {
    createQuestionAnswerSchema,
    getDefaultAnswerValues,
    isOtherOptionSelected,
    type QuestionAnswerFormValues,
} from './validation';
import EvaluationClosedPage from '../EvaluationClosed/EvaluationClosedPage';
import SurveyGuidePage from '../SurveyGuide/SurveyGuidePage';

import type { CycleQuestionnaire } from '@/types/cycle-evaluation/CycleQuestionnaire';
import type { User } from '@/types/directory/User';
import type { ReviewQuestionAnswer } from '@/types/review/ReviewAnswer';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReviewQuestionnaire } from '@/types/review/ReviewQuestionnaire';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';

import { isRefusedError } from '@/api/api';
import { useGetCycleQuestionnaire } from '@/api/cycle-evaluation-services/cycle-questionnaire';
import { useGetUsersByIds } from '@/api/directory';
import { useGetReviewAnswer, useUpdateReviewAnswer } from '@/api/review-services/review-answer';
import {
    useCreateReviewQuestionAnswer,
    useUpdateReviewQuestionAnswer,
} from '@/api/review-services/review-question-answer';
import { useGetReviewQuestionnaire } from '@/api/review-services/review-questionnaire';
import {
    useGetReviewTemplate,
    useGetReviewTemplatePreviewQuestions,
} from '@/api/review-services/review-template';
import { reviewAnswerAtom } from '@/atoms/reviewAnswer';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { QuestionType } from '@/constants/QuestionType';
import { ManagerAssessmentSkillSelectorOption } from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import { ReviewAnswerStatus } from '@/constants/review/ReviewAnswer';
import { SessionStorageKey } from '@/constants/StorageKeys';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { RouteName } from '@/navigation/types';
import { SuccessMessageAction } from '@/pages/shared/SuccessMessage/SuccessMessagePage';
import i18n from '@/plugins/i18n';
import { captureError } from '@/plugins/Sentry';
import { showErrorToast, showWarningToast } from '@/plugins/Toast';
import {
    getCurrentSelectedValues,
    getSelectedValuesFromExisting,
    transformToReviewQuestionAnswer,
} from '@/utils/reviewQuestionAnswer';

const BACKGROUND_IMAGE_URL =
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80';

export const SurveyAnswerPage = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const location = useLocation<{ questionnaireId: string; reviewee?: User }>();

    const searchParams = new URLSearchParams(location.search);

    const questionnaireId = searchParams.get('questionnaireId') || undefined;

    const revieweeId = searchParams.get('revieweeId') || undefined;
    const reviewType = searchParams.get('reviewType') || undefined;
    const isCycleEvaluation = searchParams.get('isCycleEvaluation') === 'true';
    const isTemplate = searchParams.get('isTemplate') === 'true';

    const { reviewAnswerId } = useParams<{ reviewAnswerId: string }>();

    const isManagerTemplatePreviewInit =
        isTemplate && reviewType === EvaluationType.MANAGER_EVALUATION;
    const [hasStarted, setHasStarted] = useState(isManagerTemplatePreviewInit);
    const [isLanguageReady, setIsLanguageReady] = useState(!reviewAnswerId);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState<'next' | 'previous'>('next');
    const [allAnswers, setAllAnswers] = useState<QuestionAnswerFormValues[]>([]);
    const [createdAnswers, setCreatedAnswers] = useState<Record<string, ReviewQuestionAnswer>>({});
    const [isSkipSubmitConfirmOpen, setIsSkipSubmitConfirmOpen] = useState(false);

    let cycleQuestionnaireId: string | undefined;
    let reviewTemplateId: string | undefined;
    let reviewQuestionnaireId: string | undefined;

    if (isCycleEvaluation) {
        cycleQuestionnaireId = questionnaireId;
    } else if (isTemplate) {
        reviewTemplateId = questionnaireId;
    } else {
        reviewQuestionnaireId = questionnaireId;
    }

    const setReviewAnswerAtom = useSetAtom(reviewAnswerAtom);

    const {
        data: cycleQuestionnaireData,
        isError: isCycleQuestionnaireError,
        error: cycleQuestionnaireError,
        isLoading: isCycleQuestionnaireLoading,
    } = useGetCycleQuestionnaire({ cycleQuestionnaireId });

    const {
        data: reviewTemplateData,
        isError: isReviewTemplateError,
        error: reviewTemplateError,
        isLoading: isReviewTemplateLoading,
    } = useGetReviewTemplate(reviewTemplateId);

    const isManagerTemplateWithSkillSelector = useMemo(() => {
        if (!isTemplate || !reviewTemplateData) return false;
        if (reviewTemplateData.reviewType !== EvaluationType.MANAGER_EVALUATION) return false;
        const opt = reviewTemplateData.skillSelectorOption;
        if (!opt) return false;
        return opt !== ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS;
    }, [isTemplate, reviewTemplateData]);
    const { data: previewQuestions = [] } = useGetReviewTemplatePreviewQuestions({
        limit: 10,
        enabled: isManagerTemplateWithSkillSelector,
    });

    const {
        data: reviewQuestionnaireData,
        isError: isReviewQuestionnaireError,
        error: reviewQuestionnaireError,
        isLoading: isReviewQuestionnaireLoading,
    } = useGetReviewQuestionnaire({ reviewQuestionnaireId });

    let questionnaire: CycleQuestionnaire | ReviewTemplate | ReviewQuestionnaire | undefined =
        reviewQuestionnaireData;
    let isQuestionnaireError = isReviewQuestionnaireError;
    let questionnaireError = reviewQuestionnaireError;
    let questionnaireLoading = isReviewQuestionnaireLoading;

    if (isCycleEvaluation) {
        questionnaire = cycleQuestionnaireData;
        isQuestionnaireError = isCycleQuestionnaireError;
        questionnaireError = cycleQuestionnaireError;
        questionnaireLoading = isCycleQuestionnaireLoading;
    } else if (isTemplate) {
        questionnaire = reviewTemplateData;
        isQuestionnaireError = isReviewTemplateError;
        questionnaireError = reviewTemplateError;
        questionnaireLoading = isReviewTemplateLoading;
    }

    const {
        data: reviewAnswer,
        isError: isReviewAnswerError,
        error: reviewAnswerError,
        isLoading: reviewAnswerLoading,
    } = useGetReviewAnswer({
        reviewAnswerId,
    });

    const isPureSurveyPreview = !reviewAnswerId && !!revieweeId;

    const { data: previewUsers, isLoading: previewUsersLoading } = useGetUsersByIds({
        userIds: isPureSurveyPreview ? [revieweeId] : [],
        identifier: 'survey-answer-preview',
    });

    const previewReviewee = isPureSurveyPreview ? previewUsers?.[0] : undefined;
    const usersLoading = isPureSurveyPreview ? previewUsersLoading : reviewAnswerLoading;

    const isSelfEvaluation = reviewType === EvaluationType.SELF_EVALUATION;

    const { mutateAsync: createReviewQuestionAnswer, isPending: isCreatingReviewQuestionAnswer } =
        useCreateReviewQuestionAnswer();
    const { mutateAsync: updateReviewQuestionAnswer, isPending: isUpdatingReviewQuestionAnswer } =
        useUpdateReviewQuestionAnswer();
    const { mutateAsync: updateReviewAnswer, isPending: isUpdatingReviewAnswer } =
        useUpdateReviewAnswer();

    const isSaving =
        isCreatingReviewQuestionAnswer || isUpdatingReviewQuestionAnswer || isUpdatingReviewAnswer;

    const sampleQuestions: ReviewQuestion[] = useMemo(() => {
        if (reviewAnswerId) {
            return reviewAnswer?.reviewQuestionnaire?.reviewQuestions || [];
        }

        if (questionnaireId) {
            if (isTemplate) {
                const templateQuestions = (questionnaire as ReviewTemplate)?.questions || [];
                const activeTemplateQuestions = templateQuestions.filter(
                    (q: ReviewQuestion) => !q.status || q.status === ReviewQuestionStatus.ACTIVE,
                );

                // For manager templates with a skill selector, append preview questions
                if (isManagerTemplateWithSkillSelector) {
                    return [...previewQuestions, ...activeTemplateQuestions];
                }

                return activeTemplateQuestions;
            }

            const questions = (questionnaire as ReviewQuestionnaire)?.reviewQuestions;
            return (
                questions?.filter(
                    (question: ReviewQuestion) =>
                        !question.status || question.status === ReviewQuestionStatus.ACTIVE,
                ) || []
            );
        }

        return [];
    }, [
        reviewAnswerId,
        questionnaireId,
        questionnaire,
        reviewAnswer,
        isTemplate,
        isManagerTemplateWithSkillSelector,
        previewQuestions,
    ]);

    const currentQuestion: ReviewQuestion = sampleQuestions[currentIndex];
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === sampleQuestions.length - 1;

    // Create schema for current question only
    const currentQuestionSchema = useMemo(() => {
        if (!currentQuestion) {
            return null;
        }

        return createQuestionAnswerSchema(currentQuestion);
    }, [currentQuestion]);

    // Create resolver that will be used for validation
    const resolver = useMemo(() => {
        if (!currentQuestionSchema) {
            return undefined;
        }

        return zodResolver(currentQuestionSchema);
    }, [currentQuestionSchema]);

    const { watch, setValue, reset } = useForm<QuestionAnswerFormValues>({
        resolver,
        defaultValues: currentQuestion ? getDefaultAnswerValues(currentQuestion) : undefined,
        mode: 'onChange',
    });

    const currentAnswer = watch();

    // Derive selected values from form state for rendering
    const selectedValuesForRender = getCurrentSelectedValues({
        currentAnswer,
        currentQuestion,
    });

    const handleCommentChange = useCallback(
        (value: string) => {
            setValue('comment', value);
        },
        [setValue],
    );

    const handleOtherTextChange = useCallback(
        (text: string) => {
            setValue('textValue', text);
        },
        [setValue],
    );

    const handleAnswerChange = useCallback(
        (values: string[]) => {
            switch (currentQuestion?.questionType) {
                case QuestionType.PLAIN_TEXT:
                    setValue('textValue', values[0] || '');
                    setValue('selectedValues', []);
                    break;
                case QuestionType.RATING_SCALE:
                    setValue('intValue', values[0] ? parseInt(values[0], 10) : undefined);
                    setValue('selectedValues', []);
                    break;
                case QuestionType.YES_NO: {
                    const value = values[0];
                    const defaultValue = () => {
                        if (value === 'yes') {
                            return true;
                        }

                        return value === undefined ? undefined : false;
                    };

                    setValue('selectedValues', []);
                    setValue('boolValue', defaultValue());
                    break;
                }

                default: {
                    setValue('selectedValues', values);
                    // Clear textValue when changing away from "Other" option
                    if (!isOtherOptionSelected(values, currentQuestion)) {
                        setValue('textValue', '');
                    }
                    break;
                }
            }
        },
        [currentQuestion, setValue],
    );

    const saveCurrentAnswer = async (isMandatory: boolean): Promise<boolean> => {
        const updatedAnswers = [...allAnswers];
        updatedAnswers[currentIndex] = {
            ...currentAnswer,
            selectedValues: !isMandatory ? [] : currentAnswer.selectedValues,
        };

        setAllAnswers(updatedAnswers);

        if (!reviewAnswerId) {
            return true;
        }

        const currentQuestionId = normalizeQuestionId(currentQuestion.id);

        const existingReviewQuestionAnswer =
            createdAnswers[currentQuestion.id ?? ''] ??
            reviewAnswer?.reviewQuestionAnswers?.find(
                (a) => normalizeQuestionId(a.reviewQuestionId) === currentQuestionId,
            );

        const reviewQuestionAnswer = transformToReviewQuestionAnswer({
            formValues: {
                ...currentAnswer,
                selectedValues: !isMandatory ? [] : currentAnswer.selectedValues,
            },
            reviewQuestion: currentQuestion,
            existingReviewQuestionAnswer,
            reviewAnswerId: reviewAnswerId,
            hasBeenSkipped: !isMandatory,
            comment: currentAnswer.comment,
        });

        try {
            if (existingReviewQuestionAnswer) {
                const updated = await updateReviewQuestionAnswer(reviewQuestionAnswer);
                setCreatedAnswers((prev) => ({
                    ...prev,
                    [currentQuestion.id ?? '']: updated,
                }));
            } else {
                const created = await createReviewQuestionAnswer(reviewQuestionAnswer);
                setCreatedAnswers((prev) => ({
                    ...prev,
                    [currentQuestion.id ?? '']: created,
                }));
            }
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                showErrorToast({
                    title: t('reviewAnswer.evaluationClosed.errorTitle'),
                    message: t('reviewAnswer.evaluationClosed.errorMessage'),
                });
            } else {
                captureError({
                    error: error as Error,
                    context: { tags: { area: 'survey-answer', action: 'save-question-answer' } },
                });
                showErrorToast({ message: t('common.error') });
            }
            return false;
        }

        return true;
    };

    const validateCurrentQuestion = async (): Promise<boolean> => {
        if (!currentQuestionSchema) return true;

        const result = currentQuestionSchema.safeParse(currentAnswer);

        if (!result.success) {
            const firstError = result.error.issues[0];

            if (firstError?.message) {
                const translatedMessage = t(firstError.message as Parameters<typeof t>[0]);
                showWarningToast({ message: translatedMessage });
            }

            return false;
        }

        return true;
    };

    const handleNext = async () => {
        // Always validate if question is mandatory OR if Other option is selected
        const shouldValidate =
            currentQuestion.isAnswerMandatory ||
            (currentQuestion.hasOtherOption &&
                isOtherOptionSelected(currentAnswer.selectedValues, currentQuestion));

        if (shouldValidate) {
            const isValid = await validateCurrentQuestion();

            if (!isValid) {
                return;
            }
        }

        const saved = await saveCurrentAnswer(true);
        if (!saved) {
            return;
        }
        const nextIndex = Math.min(currentIndex + 1, sampleQuestions.length - 1);
        const nextQuestion = sampleQuestions[nextIndex];
        if (nextQuestion) {
            reset(allAnswers[nextIndex] ?? getDefaultAnswerValues(nextQuestion));
        }
        setDirection('next');
        setCurrentIndex(nextIndex);
    };

    const handlePrevious = async () => {
        const saved = await saveCurrentAnswer(true);
        if (!saved) {
            return;
        }
        const previousIndex = Math.max(currentIndex - 1, 0);
        const previousQuestion = sampleQuestions[previousIndex];
        if (previousQuestion) {
            reset(allAnswers[previousIndex] ?? getDefaultAnswerValues(previousQuestion));
        }
        setDirection('previous');
        setCurrentIndex(previousIndex);
    };

    const handleSkip = async () => {
        if (isLast) {
            // Skipping the last question ends the evaluation, which the word "skip" does not
            // convey. Ask first — in preview too, so the preview shows the reviewer exactly
            // the flow a respondent gets.
            setIsSkipSubmitConfirmOpen(true);
            return;
        }

        await saveCurrentAnswer(false);
        const nextIndex = Math.min(currentIndex + 1, sampleQuestions.length - 1);
        const nextQuestion = sampleQuestions[nextIndex];
        if (nextQuestion) {
            reset(allAnswers[nextIndex] ?? getDefaultAnswerValues(nextQuestion));
        }
        setDirection('next');
        setCurrentIndex(nextIndex);
    };

    const handleConfirmSkipSubmit = async () => {
        try {
            await handleSubmit(false);
        } finally {
            setIsSkipSubmitConfirmOpen(false);
        }
    };

    const handleSubmit = async (shouldValidate: boolean = true) => {
        if (shouldValidate) {
            // Always validate if question is mandatory OR if Other option is selected
            const needsValidation =
                currentQuestion.isAnswerMandatory ||
                (currentQuestion.hasOtherOption &&
                    isOtherOptionSelected(currentAnswer.selectedValues, currentQuestion));

            if (needsValidation) {
                const isValid = await validateCurrentQuestion();

                if (!isValid || (!reviewAnswer && reviewAnswerId)) {
                    return;
                }
            }
        }

        // For preview mode, just close window after validation
        if (!reviewAnswerId) {
            navigate({
                path: `${RouteName.SuccessMessage}?action=close-preview`,
                state: {
                    title: t('reviewAnswer.successTitle'),
                    subtitle: t('reviewAnswer.closeWindow'),
                    buttonLabel: t('reviewAnswer.closePreview'),
                    actionType: SuccessMessageAction.CLOSE_WINDOW,
                },
            });
            return;
        }

        // For actual survey mode, save and submit
        const saved = await saveCurrentAnswer(shouldValidate);
        if (!saved) {
            return;
        }

        if (reviewAnswer?.isClosed) {
            return;
        }

        try {
            await updateReviewAnswer({
                id: reviewAnswer?.id || '',
                status: ReviewAnswerStatus.COMPLETED,
            });
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                showErrorToast({
                    title: t('reviewAnswer.evaluationClosed.errorTitle'),
                    message: t('reviewAnswer.evaluationClosed.errorMessage'),
                });
            } else {
                captureError({
                    error: error as Error,
                    context: { tags: { area: 'survey-answer', action: 'submit-review-answer' } },
                });
                showErrorToast({ message: t('common.error') });
            }
            return;
        }

        const isAuthenticated = Boolean(
            import.meta.env.VITE_DEV_TOKEN || localStorage.getItem('Token'),
        );

        navigate({
            path: RouteName.SuccessMessage,
            state: {
                title: t('reviewAnswer.successTitle'),
                subtitle: isAuthenticated ? undefined : t('reviewAnswer.closeWindow'),
                buttonLabel: isAuthenticated ? t('reviewAnswer.successButtonLabel') : undefined,
                actionType: isAuthenticated ? SuccessMessageAction.NAVIGATE_TO_HOME : undefined,
            },
        });
    };

    const isEvaluationUnavailable =
        (!reviewAnswerId && !questionnaireId) ||
        isRefusedError(reviewAnswerError) ||
        isRefusedError(questionnaireError);

    const fetchErrors = useMemo(
        () => [!isEvaluationUnavailable && (isQuestionnaireError || isReviewAnswerError)],
        [isEvaluationUnavailable, isQuestionnaireError, isReviewAnswerError],
    );

    useFetchErrorsHandler({
        errors: fetchErrors,
    });

    // Initialize all answers when questions are loaded
    useEffect(() => {
        if (sampleQuestions.length > 0 && allAnswers.length === 0) {
            const existingAnswersByQuestionId = new Map(
                (reviewAnswer?.reviewQuestionAnswers ?? []).map((answer) => [
                    normalizeQuestionId(answer.reviewQuestionId),
                    answer,
                ]),
            );

            const initialAnswers = sampleQuestions.map((question) => {
                // Only use existing answers when we have a reviewAnswer (editing existing answers)
                const existingAnswer =
                    reviewAnswerId && question.id
                        ? existingAnswersByQuestionId.get(normalizeQuestionId(question.id))
                        : undefined;

                if (existingAnswer) {
                    return {
                        questionId: question.id ?? '',
                        questionType: question.questionType,
                        selectedValues: getSelectedValuesFromExisting({ question, existingAnswer }),
                        intValue: existingAnswer.intValue ?? undefined,
                        boolValue: existingAnswer.boolValue ?? undefined,
                        textValue: existingAnswer.textValue ?? undefined,
                        comment: existingAnswer.comment ?? undefined,
                    } as QuestionAnswerFormValues;
                }

                // For questionnaire flow (new answers), use default values
                return getDefaultAnswerValues(question);
            });

            setAllAnswers(initialAnswers);

            // Reset form with the first question's answer immediately after initializing
            if (initialAnswers[currentIndex]) {
                reset(initialAnswers[currentIndex]);
            }
        }
    }, [sampleQuestions, reviewAnswer, reviewAnswerId, allAnswers.length, currentIndex, reset]);

    // Reset form when question changes (only after allAnswers is initialized)
    useEffect(() => {
        if (allAnswers.length > 0 && allAnswers[currentIndex]) {
            reset(allAnswers[currentIndex]);
        }
    }, [currentIndex, allAnswers, reset]);

    useEffect(() => {
        setReviewAnswerAtom(reviewAnswer);
        return () => setReviewAnswerAtom(undefined);
    }, [reviewAnswer, setReviewAnswerAtom]);

    // Honour the user's last explicit choice (set by SurveyBanner) over the BE-stored
    // preference, which may briefly lag behind after a click.
    useEffect(() => {
        if (reviewAnswer?.assignedToUser) {
            const userOverride = sessionStorage.getItem(SessionStorageKey.SURVEY_LANGUAGE_OVERRIDE);
            const userLanguage =
                userOverride ||
                reviewAnswer.assignedToUser.surveyPreferedLang ||
                reviewAnswer.assignedToUser.languagePreference;
            if (userLanguage && userLanguage !== i18n.language) {
                i18n.changeLanguage(userLanguage);
            }
        }
    }, [reviewAnswer?.assignedToUser]);

    // Mark language as ready once reviewAnswer data has finished loading
    useEffect(() => {
        if (!reviewAnswerLoading && reviewAnswerId && !isLanguageReady) {
            setIsLanguageReady(true);
        }
    }, [reviewAnswerLoading, reviewAnswerId, isLanguageReady]);

    if (questionnaireLoading || reviewAnswerLoading || usersLoading || !isLanguageReady) {
        return (
            <Skeleton className={'relative mx-auto flex max-w-5xl flex-col gap-16 px-6 py-14'}>
                <Bone className={'h-50 max-w-122'} />

                <Bone className={'h-screen'} />
            </Skeleton>
        );
    }

    if (isEvaluationUnavailable) {
        return <EvaluationClosedPage variant={'unavailable'} />;
    }

    if (reviewAnswer?.isEvaluationCompleted || reviewAnswer?.isClosed) {
        return (
            <EvaluationClosedPage
                variant={reviewAnswer.isEvaluationCompleted ? 'alreadyAnswered' : 'closed'}
            />
        );
    }

    if (!hasStarted) {
        return (
            <SurveyGuidePage
                onStart={() => setHasStarted(true)}
                isSelfEvaluation={isSelfEvaluation}
            />
        );
    }

    if (!currentQuestion) {
        return null;
    }

    const isAnswerValid = currentQuestionSchema
        ? currentQuestionSchema.safeParse(currentAnswer).success
        : true;

    const resolvedReviewee = reviewAnswerId ? reviewAnswer?.revieweeUser : previewReviewee;

    return (
        <>
            <SurveyAnswer
                reviewee={resolvedReviewee}
                showScoresToTheReviewee={reviewAnswer?.showScoresToTheReviewee}
                showCommentsToTheReviewee={reviewAnswer?.showCommentsToTheReviewee}
                question={currentQuestion}
                renderAnswer={
                    <Answer
                        key={currentQuestion.id}
                        question={currentQuestion}
                        type={currentQuestion.questionType}
                        selectedValues={selectedValuesForRender}
                        onChange={handleAnswerChange}
                        onEnterPress={handleNext}
                        reviewee={resolvedReviewee}
                    />
                }
                isFirst={isFirst}
                isLast={isLast}
                onNext={handleNext}
                onPrevious={isFirst ? undefined : handlePrevious}
                onSkip={currentQuestion.isAnswerMandatory ? undefined : handleSkip}
                onSubmit={handleSubmit}
                showComment
                commentValue={currentAnswer.comment}
                onCommentChange={handleCommentChange}
                contentKey={currentQuestion.id}
                transitionDirection={direction}
                currentIndex={currentIndex}
                totalQuestions={sampleQuestions.length}
                backgroundImageUrl={BACKGROUND_IMAGE_URL}
                isPreview={!reviewAnswerId}
                includeOther={currentQuestion.hasOtherOption}
                isOtherSelected={isOtherOptionSelected(selectedValuesForRender, currentQuestion)}
                otherText={currentAnswer.textValue}
                onOtherTextChange={handleOtherTextChange}
                isAnswerValid={isAnswerValid}
                isSaving={isSaving}
                isSelfEvaluation={isSelfEvaluation}
            />

            <ConfirmDialog
                isOpen={isSkipSubmitConfirmOpen}
                title={t('answer.submitConfirm.title')}
                message={t('answer.submitConfirm.message')}
                confirmLabel={t('answer.submitConfirm.confirm')}
                cancelLabel={t('common.cancel')}
                onConfirm={handleConfirmSkipSubmit}
                onCancel={() => setIsSkipSubmitConfirmOpen(false)}
                isLoading={isSaving}
            />
        </>
    );
};
